import isEmpty from 'lodash/isEmpty';

import { FormDocument, FormDocumentAttachment } from '@/features/ui/DocumentsData/model/types';

import {
  addDocumentAttachments,
  AttachmentPayload,
  pushAttachment,
  toArray,
  toStringValue,
} from '@/shared/lib/helpers/payloadHelpers';
import { IntegrationSystem } from '@/shared/types';

import { ResponseInsuranceOrder } from '@/modules/bia/shared/model/types';

import { DocumentTypeEntity, FormDataType } from '@/api/types';

export const collectAttachments = (formData: FormDataType, hasActualAddress: boolean): AttachmentPayload[] => {
  const attachments: AttachmentPayload[] = [];

  pushAttachment(attachments, formData.weaponRightPhoto as FormDocumentAttachment, 'weaponRightPhoto');
  pushAttachment(attachments, formData.weaponLeftPhoto as FormDocumentAttachment, 'weaponLeftPhoto');

  const expertResearchAttachment = Array.isArray(formData.expertResearch)
    ? (formData.expertResearch[0] as FormDocumentAttachment | undefined)
    : (formData.expertResearch as FormDocumentAttachment | undefined);

  pushAttachment(attachments, expertResearchAttachment as FormDocumentAttachment, 'expertResearch', 'default', {
    date: toStringValue(formData.examinationDate),
    auth_dep: toStringValue(formData.expertOrganization),
  });

  pushAttachment(attachments, formData.photoCard as FormDocumentAttachment, 'photoCard');

  const rnokppAttachment = Array.isArray(formData.rnokppDoc)
    ? (formData.rnokppDoc[0] as FormDocumentAttachment | undefined)
    : (formData.rnokppDoc as FormDocumentAttachment | undefined);

  pushAttachment(attachments, rnokppAttachment, 'rnokppDoc');

  addDocumentAttachments(
    attachments,
    toArray<FormDocument>(formData.personPassportDocument as Nullable<FormDocument | FormDocument[]>),
    'personPassportDocument',
  );
  addDocumentAttachments(
    attachments,
    toArray<FormDocument>(formData.declaredDocRent as Nullable<FormDocument | FormDocument[]>),
    'declaredDocRent',
    'default',
    'REGISTRATION',
  );

  if (hasActualAddress) {
    addDocumentAttachments(
      attachments,
      toArray<FormDocument>(formData.actualPlaceOfResidence as Nullable<FormDocument | FormDocument[]>),
      'actualPlaceOfResidence',
      'default',
      'LIVING',
    );
  }

  if (formData.confirmInsuranceDocuments) {
    addDocumentAttachments(
      attachments,
      toArray<FormDocument>(formData.confirmInsuranceDocuments as Nullable<FormDocument | FormDocument[]>),
      'confirmInsuranceDocuments',
    );
  }

  if (!isEmpty(formData.selectedInsurances)) {
    const { code, id, label } =
      formData.insuranceDocumentMetadata as unknown as DocumentTypeEntity<IntegrationSystem.ERZ>;

    (formData.selectedInsurances as ResponseInsuranceOrder[]).forEach((insurance, index) => {
      const { date_from, date_to, number } = insurance;
      attachments.push({
        id: id!,
        typeDoc: label!,
        fileName: label!,
        code,
        flag: 'default',
        info: {
          date: toStringValue(date_from),
          expirationDate: toStringValue(date_to),
          serialNumberDoc: toStringValue(number),
        },
        formField: `confirmInsuranceDocuments.${index}.attachments`,
        files: [],
      });
    });
  }

  toArray<FormDocument>(formData.additionalDocuments as Nullable<FormDocument | FormDocument[]>).forEach(
    (doc, index) => {
      pushAttachment(
        attachments,
        doc?.attachments,
        doc?.attachments?.formField ?? `additionalDocuments.${index}.attachments`,
        'additional',
        doc?.info as Record<string, unknown>,
      );
    },
  );

  return attachments;
};
